import React, {
  createContext,
  Dispatch,
  FunctionComponent,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import {
  DietPreference,
  Ethnicity,
  Gender,
  useCreateUserInformationMutation,
} from "../../../api/smartmelts-api/graphql/generated";
import { useUser } from "../../../components/user/user-context";
import { useEnum } from "../../../hooks/use-enum";
import { DateTime } from "luxon";
import lodash from "lodash";

interface IOnboardingContextProviderProps {}

interface IOnboardingContext {
  activeStep: number;
  setActiveStep: Dispatch<SetStateAction<number>>;
  setFirstname: Dispatch<SetStateAction<string>>;
  firstname: string;
  setLastname: Dispatch<SetStateAction<string>>;
  lastname: string;
  setGender: (newValue: Gender) => void;
  gender: Gender;
  setWeight: Dispatch<SetStateAction<number | null>>;
  weight: number | null;
  setDateOfBirth: Dispatch<SetStateAction<string>>;
  dateOfBirth: string;
  setHeight: Dispatch<SetStateAction<number | null>>;
  height: number | null;
  ethnicity: Ethnicity;
  setEthnicity: (newValue: Ethnicity) => void;
  dietPreference: DietPreference;
  setDietPreference: (newValue: DietPreference) => void;
  setOnboarded: Dispatch<SetStateAction<boolean>>;
  onboarded: boolean;
  genderValid: boolean;
  ethnicityValid: boolean;
  dietPreferenceValid: boolean;
  weightValid: boolean;
  heightValid: boolean;
  dateOfBirthDate: DateTime;
  createUserInformation: () => void;
  createUserInformationLoading: boolean;
  allInputsValid: boolean;
}

export const OnboardingContext = createContext<IOnboardingContext>({} as IOnboardingContext);

export const OnboardingContextProvider: FunctionComponent<IOnboardingContextProviderProps> = (props) => {
  const { children } = props;
  const { user } = useUser();
  const [activeStep, setActiveStep] = useState<number>(1);
  const [onboarded, setOnboarded] = useState<boolean>(false);
  const [firstname, setFirstname] = useState(user?.firstName || "");
  const [lastname, setLastname] = useState(user?.lastName || "");
  const [gender, setGender, genderValid] = useEnum<Gender>(Gender.MALE, Gender);
  const [weight, setWeight] = useState<number | null>(null);
  const [dateOfBirth, setDateOfBirth] = useState<string>("");
  const [height, setHeight] = useState<number | null>(null);
  const [ethnicity, setEthnicity, ethnicityValid] = useEnum<Ethnicity>(Ethnicity.EUROPEAN, Ethnicity);
  const [dietPreference, setDietPreference, dietPreferenceValid] = useEnum<DietPreference>(
    DietPreference.STANDARD,
    DietPreference,
  );
  const { fetchUser } = useUser();
  const [createUserInformationMutation, { loading: createUserInformationLoading }] = useCreateUserInformationMutation({
    onCompleted: () => fetchUser(),
  });

  const dateOfBirthDate = useMemo(() => DateTime.fromISO(dateOfBirth), [dateOfBirth]);

  const weightValid = useMemo(() => !lodash.isNaN(weight) && weight! > 0, [weight]);
  const heightValid = useMemo(() => !lodash.isNaN(height) && height! > 0, [height]);

  const allInputsValid = useMemo(
    () =>
      !!firstname &&
      !!lastname &&
      dateOfBirthDate.isValid &&
      genderValid &&
      ethnicityValid &&
      weightValid &&
      heightValid &&
      dietPreferenceValid,
    [firstname, lastname, dateOfBirthDate, genderValid, ethnicityValid, weightValid, heightValid, dietPreferenceValid],
  );

  const createUserInformation = useCallback(() => {
    createUserInformationMutation({
      variables: {
        firstname,
        lastname,
        gender,
        weight: `${weight}`,
        dateOfBirth,
        height: `${height}`,
        ethnicity,
        dietPreference,
      },
    });
  }, [
    createUserInformationMutation,
    firstname,
    lastname,
    gender,
    weight,
    dateOfBirth,
    height,
    ethnicity,
    dietPreference,
  ]);

  return (
    <OnboardingContext.Provider
      value={{
        activeStep,
        setActiveStep,
        setFirstname,
        setLastname,
        setEthnicity,
        setGender,
        setWeight,
        setDateOfBirth,
        setHeight,
        setOnboarded,
        onboarded,
        firstname,
        lastname,
        gender,
        genderValid,
        ethnicityValid,
        weightValid,
        heightValid,
        weight,
        dateOfBirth,
        height,
        ethnicity,
        dateOfBirthDate,
        createUserInformation,
        createUserInformationLoading,
        allInputsValid,
        dietPreference,
        dietPreferenceValid,
        setDietPreference,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};
